import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  BusinessHours,
  TimedItem,
  TRACK_INSET,
  computeWeekLayout,
  startOfWeek
} from './weekAgendaLayout';
import './AdminWeekAgenda.css';

export interface AgendaReservation extends TimedItem {
  status: string;
  reference_code?: string | null;
  reserver_name: string;
  reserver_email: string;
  bike?: {
    id?: number;
    asset_code: string;
  };
}

interface AdminWeekAgendaProps<T extends AgendaReservation> {
  reservations: T[];
  isLoading: boolean;
  onSelectReservation: (reservation: T) => void;
}

interface BikeColour {
  border: string;
  background: string;
  text: string;
}

const DEFAULT_BUSINESS_HOURS: BusinessHours = { start: '10:00', end: '17:00' };

// Vast palet; IJsselheem-blauw eerst. Bij meer fietsen dan kleuren begint het
// opnieuw — de fietscode op het blok houdt het dan nog leesbaar.
const BIKE_COLOURS: BikeColour[] = [
  { border: '#25377f', background: 'rgba(37, 55, 127, 0.14)', text: '#25377f' },
  { border: '#0f766e', background: 'rgba(15, 118, 110, 0.14)', text: '#0f5f59' },
  { border: '#b45309', background: 'rgba(180, 83, 9, 0.14)', text: '#9a3412' },
  { border: '#7c3aed', background: 'rgba(124, 58, 237, 0.14)', text: '#5b21b6' },
  { border: '#be123c', background: 'rgba(190, 18, 60, 0.14)', text: '#9f1239' },
  { border: '#0369a1', background: 'rgba(3, 105, 161, 0.14)', text: '#075985' },
  { border: '#4d7c0f', background: 'rgba(77, 124, 15, 0.14)', text: '#3f6212' },
  { border: '#a16207', background: 'rgba(161, 98, 7, 0.14)', text: '#854d0e' }
];

const FALLBACK_COLOUR: BikeColour = BIKE_COLOURS[0];

/** Reserveringen zonder code vallen terug op het volgnummer. */
export const referenceLabel = (reservation: AgendaReservation): string =>
  reservation.reference_code ? reservation.reference_code : `#${reservation.id}`;

const statusLabels: Record<string, string> = {
  pending: 'In afwachting',
  confirmed: 'Bevestigd',
  completed: 'Voltooid',
  cancelled: 'Geannuleerd',
  no_show: 'Niet verschenen'
};

export const statusLabel = (status: string): string => statusLabels[status] || status;

const bikeKey = (reservation: AgendaReservation): string => {
  if (reservation.bike?.id != null) return `id-${reservation.bike.id}`;
  if (reservation.bike?.asset_code) return `code-${reservation.bike.asset_code}`;
  return 'unknown';
};

const bikeColourForIndex = (index: number): BikeColour =>
  BIKE_COLOURS[((index % BIKE_COLOURS.length) + BIKE_COLOURS.length) % BIKE_COLOURS.length];

function AdminWeekAgenda<T extends AgendaReservation>({
  reservations,
  isLoading,
  onSelectReservation
}: AdminWeekAgendaProps<T>): JSX.Element {
  const [weekStart, setWeekStart] = useState<moment.Moment>(() => startOfWeek(new Date()));
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);

  const visibleReservations = useMemo(
    () => reservations.filter(reservation => showCancelled || reservation.status !== 'cancelled'),
    [reservations, showCancelled]
  );

  const layout = useMemo(
    () => computeWeekLayout(weekStart, DEFAULT_BUSINESS_HOURS, visibleReservations),
    [weekStart, visibleReservations]
  );

  // Kleurindex over alle meegegeven reserveringen (niet alleen deze week),
  // gesorteerd op fietscode, zodat een fiets dezelfde kleur houdt bij bladeren.
  const bikeColourByKey = useMemo(() => {
    const bikes = new Map<string, string>();
    reservations.forEach(reservation => {
      const key = bikeKey(reservation);
      if (key === 'unknown') return;
      const code = reservation.bike?.asset_code || key;
      if (!bikes.has(key)) bikes.set(key, code);
    });

    const ordered = Array.from(bikes.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], 'nl', { sensitivity: 'base' })
    );

    const colours = new Map<string, BikeColour>();
    ordered.forEach(([key], index) => {
      colours.set(key, bikeColourForIndex(index));
    });
    return colours;
  }, [reservations]);

  const colourFor = (reservation: AgendaReservation): BikeColour =>
    bikeColourByKey.get(bikeKey(reservation)) || FALLBACK_COLOUR;

  // Per fiets die in de zichtbare week voorkomt, voor de legenda.
  const weekBikeLegend = useMemo(() => {
    const bikes = new Map<string, { code: string; colour: BikeColour }>();
    layout.days.forEach(day => {
      day.blocks.forEach(block => {
        const reservation = block.item;
        const key = bikeKey(reservation);
        if (key === 'unknown' || bikes.has(key)) return;
        bikes.set(key, {
          code: reservation.bike?.asset_code || 'Onbekende fiets',
          colour: bikeColourByKey.get(key) || FALLBACK_COLOUR
        });
      });
    });
    return Array.from(bikes.values()).sort((a, b) =>
      a.code.localeCompare(b.code, 'nl', { sensitivity: 'base' })
    );
  }, [layout, bikeColourByKey]);

  // Zoeken: passende blokken blijven in kleur, de rest wordt grijs.
  // Het oude volgnummer blijft vindbaar (exact).
  const query = searchQuery.trim().toLowerCase();
  const matchesQuery = (reservation: T): boolean => {
    if (!query) return true;
    const idQuery = query.replace(/^#/, '');
    const matchesOldId = /^\d+$/.test(idQuery) && String(reservation.id) === idQuery;
    return (
      reservation.reserver_name.toLowerCase().includes(query) ||
      reservation.reserver_email.toLowerCase().includes(query) ||
      referenceLabel(reservation).toLowerCase().includes(query) ||
      matchesOldId
    );
  };

  const isCurrentWeek = weekStart.isSame(startOfWeek(new Date()), 'day');

  const shiftWeek = (weeks: number): void => {
    setWeekStart(current => current.clone().add(weeks, 'weeks'));
  };

  return (
    <div className="week-agenda">
      <div className="week-agenda-toolbar">
        <div className="week-agenda-nav">
          <button type="button" className="week-agenda-nav-btn" onClick={() => shiftWeek(-1)}>
            ← Vorige week
          </button>
          <div className="week-agenda-range">
            <strong>{layout.rangeLabel}</strong>
            <span>Week {weekStart.isoWeek()}</span>
          </div>
          <button type="button" className="week-agenda-nav-btn" onClick={() => shiftWeek(1)}>
            Volgende week →
          </button>
          <button
            type="button"
            className="week-agenda-today-btn"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            disabled={isCurrentWeek}
          >
            Deze week
          </button>
        </div>

        <div className="week-agenda-filters">
          <div className="week-agenda-search">
            <label htmlFor="agenda-search">Zoeken:</label>
            <input
              type="text"
              id="agenda-search"
              placeholder="Naam, e-mail of reserveringsnummer..."
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
            />
          </div>
          <label className="week-agenda-checkbox" htmlFor="agenda-show-cancelled">
            <input
              type="checkbox"
              id="agenda-show-cancelled"
              checked={showCancelled}
              onChange={event => setShowCancelled(event.target.checked)}
            />
            <span>Ook geannuleerde reserveringen tonen</span>
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="week-agenda-status">Reserveringen worden geladen...</div>
      ) : (
        <>
          <div className="week-agenda-scroll">
            <div className="week-agenda-grid">
              <div className="week-agenda-corner" />
              {layout.days.map(day => (
                <div
                  key={`head-${day.key}`}
                  className={`week-agenda-day-head ${day.isToday ? 'is-today' : ''}`}
                >
                  <span className="week-agenda-weekday">{day.weekdayLabel}</span>
                  <span className="week-agenda-date">{day.dateLabel}</span>
                </div>
              ))}

              <div
                className="week-agenda-hours"
                style={
                  {
                    height: `${layout.trackHeight}px`,
                    '--week-agenda-inset': `${TRACK_INSET}px`
                  } as React.CSSProperties
                }
              >
                <div className="week-agenda-hours-inner">
                  {layout.hourMarkers.map(hour => (
                    <span
                      key={hour.key}
                      className="week-agenda-hour-label"
                      style={{ top: `${hour.topPercent}%` }}
                    >
                      {hour.label}
                    </span>
                  ))}
                </div>
              </div>

              {layout.days.map(day => (
                <div
                  key={`body-${day.key}`}
                  className={`week-agenda-day ${day.isToday ? 'is-today' : ''}`}
                  style={
                    {
                      height: `${layout.trackHeight}px`,
                      '--week-agenda-inset': `${TRACK_INSET}px`
                    } as React.CSSProperties
                  }
                >
                  <div className="week-agenda-lines">
                    {layout.hourMarkers.map(hour => (
                      <span
                        key={`${day.key}-${hour.key}`}
                        className="week-agenda-hour-line"
                        style={{ top: `${hour.topPercent}%` }}
                      />
                    ))}
                  </div>

                  <div className="week-agenda-blocks">
                    {day.blocks.map(block => {
                      const reservation = block.item;
                      const time = `${block.startLabel} - ${block.endLabel}`;
                      const bikeCode = reservation.bike?.asset_code;
                      const colour = colourFor(reservation);
                      const metaParts = [
                        referenceLabel(reservation),
                        bikeCode,
                        time
                      ].filter(Boolean);
                      const description = [
                        reservation.reserver_name,
                        `reservering ${referenceLabel(reservation)}`,
                        time,
                        block.continuesBefore || block.continuesAfter
                          ? 'loopt door op een andere dag'
                          : null,
                        bikeCode ? `fiets ${bikeCode}` : null,
                        statusLabel(reservation.status)
                      ]
                        .filter(Boolean)
                        .join(', ');

                      return (
                        <button
                          key={block.key}
                          type="button"
                          className={[
                            'week-agenda-block',
                            reservation.status === 'cancelled' ? 'is-cancelled' : '',
                            matchesQuery(reservation) ? '' : 'is-dimmed'
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={
                            {
                              top: `${block.topPercent}%`,
                              height: `${block.heightPercent}%`,
                              left: `calc(${block.leftPercent}% + 2px)`,
                              width: `calc(${block.widthPercent}% - 4px)`,
                              '--block-border': colour.border,
                              '--block-bg': colour.background,
                              '--block-text': colour.text
                            } as React.CSSProperties
                          }
                          title={description}
                          aria-label={description}
                          onClick={() => onSelectReservation(reservation)}
                        >
                          <span className="week-agenda-block-code">
                            {reservation.reserver_name}
                          </span>
                          <span className="week-agenda-block-meta">
                            {metaParts.join(' · ')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {layout.blockCount === 0 && (
            <p className="week-agenda-empty">Geen reserveringen in deze week.</p>
          )}

          <div className="week-agenda-legend">
            {weekBikeLegend.map(bike => (
              <span key={bike.code} className="legend-item">
                <span
                  className="legend-swatch"
                  style={
                    {
                      '--block-border': bike.colour.border,
                      '--block-bg': bike.colour.background
                    } as React.CSSProperties
                  }
                />
                {bike.code}
              </span>
            ))}
            {showCancelled && (
              <span className="legend-item">
                <span className="legend-swatch legend-swatch-cancelled" /> Geannuleerd
              </span>
            )}
            <span className="legend-item">Klik op een blok voor de gegevens</span>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminWeekAgenda;
